import React, {useEffect, useRef, useState} from 'react'
import {getMemberStatusSummaryApi} from '../../../modules/Dashboard/DashboardCURD'

/* ─── Types ─── */
interface MemberStatusSummaryData {
  totalMembers: number
  activeVerified: number
  notVerified: number
  inactive: number
  deleted: number
  forgotPwdPending: number
  liveJaapNow: number
  zeroActivity: number
  totalDigitalRamName: number
  totalBookRamName: number
  grandTotalRamName: number
  indiaMembers: number
  otherCountryMembers: number
}
interface MemberStatusSummaryResp {
  isSuccess: boolean
  message: string
  data: MemberStatusSummaryData | null
}
interface IState {
  loading: boolean
  data: MemberStatusSummaryData | null
  error: string
}

// Add this helper at the top of the file, outside the component
function formatIndian(v: number): string {
  if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(2)} Cr`
  if (v >= 100_000) return `${(v / 100_000).toFixed(2)} L`
  //   if (v >= 1_000)      return `${(v / 1_000).toFixed(1)} K`
  return v.toLocaleString('en-IN')
}

/* ─── Count-up hook ─── */
function useCountUp(target: number, duration = 1200, delay = 0) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!target) {
      el.textContent = '0'
      return
    }
    const t = setTimeout(() => {
      const start = Date.now()
      const step = () => {
        const p = Math.min((Date.now() - start) / duration, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        el.textContent = formatIndian(Math.round(ease * target))
        if (p < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }, delay)
    return () => clearTimeout(t)
  }, [target, duration, delay])
  return ref
}

/* ─── API ─── */
async function fetchMemberStatusSummary(): Promise<MemberStatusSummaryResp> {
  const response = await getMemberStatusSummaryApi()
  return response.data
}

/* ─── Total banner card ─── */
interface TotalCardProps {
  label: string
  value: number
  iconClass: string
  bg: string
  color: string
  delay?: number
}
const TotalCard: React.FC<TotalCardProps> = ({label, value, iconClass, bg, color, delay = 0}) => {
  const spanRef = useCountUp(value, 1300, delay)
  return (
    <div className='col-12 col-sm-4'>
      <div className='rounded-3 p-3 h-100' style={{background: bg}}>
        <i className={`${iconClass} fs-4 mb-2 d-block`} style={{color}} />
        <p className='fw-bold mb-1 lh-1' style={{fontSize: 22, color}}>
          <span ref={spanRef}>0</span>
        </p>
        <small style={{color, opacity: 0.8}}>{label}</small>
      </div>
    </div>
  )
}

/* ─── Stat row ─── */
interface StatRowProps {
  label: string
  value: number
  iconClass: string
  accentColor: string
  iconBg: string
  delay?: number
  linkTo?: string
}
const StatRow: React.FC<StatRowProps> = ({
  label,
  value,
  iconClass,
  accentColor,
  iconBg,
  delay = 0,
  linkTo,
}) => {
  const spanRef = useCountUp(value, 1100, delay)
  return (
    <div
      className='d-flex align-items-center gap-2 gap-md-3 px-3 py-2 rounded-3 bg-white'
      style={{
        border: '1px solid #f3f4f6',
        cursor: linkTo ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
      }}
      onClick={() => linkTo && window.location.assign(linkTo)}
      onMouseEnter={(e) => {
        if (linkTo)
          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 10px rgba(0,0,0,0.07)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
      }}
    >
      {/* icon */}
      <div
        className='d-flex align-items-center justify-content-center flex-shrink-0 rounded-2'
        style={{width: 32, height: 32, background: iconBg}}
      >
        <i className={iconClass} style={{color: accentColor, fontSize: 14}} />
      </div>
      {/* label */}
      <span className='flex-fill text-muted fw-medium' style={{fontSize: 13}}>
        {label}
      </span>
      {/* value */}
      <span className='fw-bold' style={{fontSize: 15, color: '#111827'}}>
        <span ref={spanRef}>0</span>
      </span>
      {linkTo && <i className='bi bi-chevron-right text-muted' style={{fontSize: 10}} />}
    </div>
  )
}

/* ─── Main component ─── */
const MemberStatusWidget: React.FC<{className?: string}> = ({className = ''}) => {
  const [state, setState] = useState<IState>({loading: true, data: null, error: ''})

  useEffect(() => {
    setState((s) => ({...s, loading: true, error: ''}))
    fetchMemberStatusSummary()
      .then((resp) => {
        if (resp.isSuccess && resp.data) setState({loading: false, data: resp.data, error: ''})
        else setState({loading: false, data: null, error: resp.message || 'No data'})
      })
      .catch((err) => setState({loading: false, data: null, error: String(err)}))
  }, [])

  const d = state.data

  return (
    <>
      <style>{`
        @keyframes msw-shimmer { 0%,100%{opacity:1} 50%{opacity:.35} }
        .msw-shimmer { animation:msw-shimmer 1.4s ease-in-out infinite; background:#f3f4f6; border-radius:8px; }
      `}</style>

      <div className={`card border-0 shadow-sm rounded-4 overflow-hidden h-100 ${className}`}>
        {/* ── Header ── */}
        <div
          className='card-header border-0 py-3 px-4 d-flex align-items-center justify-content-between'
          style={{background: 'linear-gradient(135deg,#1D9E75 0%,#22c55e 100%)'}}
        >
          <div>
            <h3 className='card-title fw-bolder text-white mb-0 fs-5'>
              <i className='bi bi-people-fill me-2' />
              Member Status Summary
            </h3>
            <p className='text-white mb-0 small' style={{opacity: 0.8}}>
              Platform-wide member overview
            </p>
          </div>
          <i className='bi bi-person-lines-fill text-white fs-3' style={{opacity: 0.6}} />
        </div>

        <div className='card-body py-3 px-3 px-md-4'>
          {state.loading ? (
            <>
              {/* Banner skeletons */}
              <div className='row g-2 mb-3'>
                {[0.0, 0.15, 0.3].map((delay, i) => (
                  <div key={i} className='col-12 col-sm-4'>
                    <div
                      className='msw-shimmer w-100'
                      style={{height: 88, animationDelay: `${delay}s`}}
                    />
                  </div>
                ))}
              </div>
              {/* Divider shimmer */}
              <div className='msw-shimmer mb-3' style={{width: 130, height: 10}} />
              {/* Row skeletons */}
              <div className='d-flex flex-column gap-2'>
                {Array.from({length: 8}, (_, i) => (
                  <div
                    key={i}
                    className='d-flex align-items-center gap-3 px-3 py-2 rounded-3 bg-white'
                    style={{border: '1px solid #f3f4f6'}}
                  >
                    <div
                      className='msw-shimmer flex-shrink-0 rounded-2'
                      style={{width: 32, height: 32, animationDelay: `${i * 0.08}s`}}
                    />
                    <div
                      className='msw-shimmer flex-fill rounded-2'
                      style={{height: 12, animationDelay: `${i * 0.08}s`}}
                    />
                    <div className='msw-shimmer rounded-2' style={{width: 52, height: 14}} />
                  </div>
                ))}
              </div>
            </>
          ) : state.error || !d ? (
            <div
              className='d-flex align-items-center justify-content-center text-danger small'
              style={{minHeight: 320}}
            >
              <i className='bi bi-exclamation-triangle me-2' />
              {state.error || 'No data found'}
            </div>
          ) : (
            <>
              {/* ── Banner cards: stack on mobile (col-12), side-by-side on sm+ (col-sm-4) ── */}
              <div className='row g-2 mb-3'>
                <TotalCard
                  label='Grand Total Ram Names'
                  value={d.grandTotalRamName}
                  iconClass='bi bi-infinity'
                  bg='linear-gradient(135deg,#fef3c7,#fde68a)'
                  color='#92400e'
                  delay={0}
                />
                <TotalCard
                  label='Digital Ram Names'
                  value={d.totalDigitalRamName}
                  iconClass='bi bi-phone-fill'
                  bg='linear-gradient(135deg,#ede9fe,#ddd6fe)'
                  color='#5b21b6'
                  delay={200}
                />
                <TotalCard
                  label='Book Ram Names'
                  value={d.totalBookRamName}
                  iconClass='bi bi-book-fill'
                  bg='linear-gradient(135deg,#dbeafe,#bfdbfe)'
                  color='#1e3a8a'
                  delay={350}
                />
              </div>

              {/* ── Divider ── */}
              <p
                className='text-uppercase fw-bold mb-2'
                style={{fontSize: 11, color: '#9ca3af', letterSpacing: '.07em'}}
              >
                Member Breakdown
              </p>

              {/* ── Stat rows ── */}
              <div className='d-flex flex-column gap-2'>
                <StatRow
                  label='Total Members'
                  value={d.totalMembers}
                  iconClass='bi bi-people-fill'
                  accentColor='#374151'
                  iconBg='#f3f4f6'
                  delay={80}
                />

                <StatRow
                  label='India Members'
                  value={d.indiaMembers}
                  iconClass='bi bi-geo-alt-fill'
                  accentColor='#16a34a'
                  iconBg='#f0fdf4'
                  delay={100}
                />

                <StatRow
                  label='Other Country Members'
                  value={d.otherCountryMembers}
                  iconClass='bi bi-globe2'
                  accentColor='#2563eb'
                  iconBg='#eff6ff'
                  delay={120}
                />

                <StatRow
                  label='Active & Verified'
                  value={d.activeVerified}
                  iconClass='bi bi-patch-check-fill'
                  accentColor='#1D9E75'
                  iconBg='#E1F5EE'
                  delay={150}
                />
                <StatRow
                  label='Live Jaap Now'
                  value={d.liveJaapNow}
                  iconClass='bi bi-broadcast'
                  accentColor='#22c55e'
                  iconBg='#f0fdf4'
                  delay={210}
                />
                <StatRow
                  label='Mobile Number Not Verified'
                  value={d.notVerified}
                  iconClass='bi bi-person-x'
                  accentColor='#f59e0b'
                  iconBg='#fffbeb'
                  delay={270}
                />
                <StatRow
                  label='Inactive'
                  value={d.inactive}
                  iconClass='bi bi-person-dash'
                  accentColor='#6b7280'
                  iconBg='#f9fafb'
                  delay={320}
                />
                <StatRow
                  label='Zero Activity'
                  value={d.zeroActivity}
                  iconClass='bi bi-slash-circle'
                  accentColor='#ef4444'
                  iconBg='#fef2f2'
                  delay={370}
                />
                <StatRow
                  label='Forgot Password Pending'
                  value={d.forgotPwdPending}
                  iconClass='bi bi-key-fill'
                  accentColor='#8b5cf6'
                  iconBg='#f5f3ff'
                  delay={420}
                />
                <StatRow
                  label='Deleted'
                  value={d.deleted}
                  iconClass='bi bi-trash-fill'
                  accentColor='#dc2626'
                  iconBg='#fef2f2'
                  delay={470}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export {MemberStatusWidget}
